import { createWorkStep } from '@/models';
import { ServiceContainer } from '@/providers';
import { WorkLoadable } from '@/stores';
import { UpdatableViewModelState } from '@/viewmodels/shared';
import { WorkCompassSteps } from '@buf/studyo_studyo-today-planners.bufbuild_es/studyo/today/planners/v1/resources/work_compass_steps_pb';
import { Work } from '@buf/studyo_studyo-today-planners.bufbuild_es/studyo/today/planners/v1/resources/work_pb';
import { captureException } from '@sentry/react';
import { action, computed, makeObservable, observable, override, runInAction } from 'mobx';
import { v4 as uuidV4 } from 'uuid';
import {
  AppBaseUpdatableDialogViewModel,
  CancelDialogActionButtonConfiguration,
  DialogActionButtonConfiguration,
  SaveDialogActionButtonConfiguration,
  UpdatableDialogViewModel
} from '../utils';

export interface AiSuggestedStepsViewModel extends UpdatableDialogViewModel {
  readonly reason: string | undefined;
  readonly suggestedSteps: string[] | undefined;
  readonly selectedStepIndexes: Set<number>;
  readonly canSave: boolean;

  changeStepSelection: (index: number, isSelected: boolean) => void;
  save: () => Promise<void>;
  cancel: () => Promise<void>;
}

export class AppAiSuggestedStepsViewModel extends AppBaseUpdatableDialogViewModel implements AiSuggestedStepsViewModel {
  @observable private _isApplying = false;
  @observable private _selectedStepIndexes = new Set<number>();

  private _saveButtonConfig = new SaveDialogActionButtonConfiguration('main', this._localization, () => this.save());
  private _cancelButtonConfig = new CancelDialogActionButtonConfiguration('main', this._localization, () =>
    this.cancel()
  );

  @computed
  private get workLoadable(): WorkLoadable {
    return this._workStore.getWorkLoadable(this._workId);
  }

  constructor(
    private readonly _workId: string,
    private readonly _plannerId: string,
    private readonly _onSuccess: () => Promise<void>,
    private readonly _onCancel: () => Promise<void>,
    private readonly _workStore = ServiceContainer.services.workStore,
    private readonly _plannerStore = ServiceContainer.services.plannerStore,
    localization = ServiceContainer.services.localization
  ) {
    super(localization, _onCancel);
    makeObservable(this);
  }

  @computed
  get work(): Work {
    return this.workLoadable.data;
  }

  @computed
  get workCompassSteps(): WorkCompassSteps {
    const compassSteps = this.workLoadable.data?.compassSteps;
    if (compassSteps == null) {
      throw new Error('WorkCompassSteps data is undefined');
    }
    return compassSteps;
  }

  @override
  get actions(): DialogActionButtonConfiguration[] {
    this._cancelButtonConfig.isEnabled = !this._isApplying;
    this._saveButtonConfig.isEnabled = !this._isApplying && this.canSave;
    this._saveButtonConfig.showLoading = this._isApplying;
    return [this._cancelButtonConfig, this._saveButtonConfig];
  }

  @computed
  get state(): UpdatableViewModelState {
    return this.workLoadable.state;
  }

  @computed
  get isApplying(): boolean {
    return this._isApplying;
  }

  @computed
  get isSubmitting(): boolean {
    return this.isApplying;
  }

  @computed
  get hasData(): boolean {
    return this.workLoadable.hasData;
  }

  @computed
  get hasChanges(): boolean {
    return this._selectedStepIndexes.size > 0;
  }

  async reloadData() {
    await this.loadData();
  }

  private async loadData() {
    this.clearError();

    try {
      await this.workLoadable.fetch(true);
    } catch (e) {
      captureException(e);
      const error = e as Error;
      runInAction(() => {
        this._error = error.message;
      });
    }
  }

  @computed
  get reason(): string | undefined {
    return this.workCompassSteps?.reason;
  }

  @computed
  get suggestedSteps(): string[] | undefined {
    return this.workCompassSteps?.suggestedSteps.map((step) => step);
  }

  @computed
  get selectedStepIndexes(): Set<number> {
    return this._selectedStepIndexes;
  }

  @computed
  get canSave(): boolean {
    return !this._isApplying && this.hasChanges;
  }

  @action
  changeStepSelection(index: number, isSelected: boolean): void {
    if (isSelected) {
      this._selectedStepIndexes.add(index);
    } else {
      this._selectedStepIndexes.delete(index);
    }
  }

  cancel() {
    return this._onCancel();
  }

  @action
  async save() {
    if (!this.canSave) {
      return;
    }

    this._isApplying = true;
    this._error = undefined;

    try {
      const existingSteps = this.workLoadable.data?.steps ?? [];
      const selectedSteps = this.suggestedSteps?.filter((_, index) => this._selectedStepIndexes.has(index)) ?? [];
      const newSteps = selectedSteps.map((stepTitle) =>
        createWorkStep({
          id: uuidV4(),
          title: stepTitle,
          duration: 0,
          isCompleted: false
        })
      );
      const allSteps = [...existingSteps, ...newSteps];
      await this._workStore.setWorkSteps(this._workId, allSteps, this.work.syncToken);
      void this._plannerStore.fetchPlannerContents(this._plannerId);
      await this._onSuccess();
    } catch (e) {
      captureException(e);
      const error = e as Error;
      const strings = this._localization.localizedStrings.work.details;

      runInAction(() => {
        this._error = strings.planErrorMessage(error.message);
        this._isApplying = false;
      });
    }
  }
}
